import { Fragment, ReactNode } from 'react';
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    Transition,
    TransitionChild,
} from '@headlessui/react';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { twMerge } from 'tailwind-merge';
import Button, { colors } from './Button';
import { RowTabStopContext } from './row-tab-stop';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';

export const modalSizes = {
    SMALL: 'w-[400px]',
    SMALL_MEDIUM: 'w-[450px]',
    MEDIUM: 'w-[600px]',
    LARGE: 'w-[800px]',
};

type ModalSize = (typeof modalSizes)[keyof typeof modalSizes];

/**
 * A dialog must have a name, or a screen reader says only "dialog" when it
 * opens. `header` renders as the `DialogTitle`, which HeadlessUI wires up as
 * the dialog's `aria-labelledby`. A modal that shows no title names itself
 * through `ariaLabel` instead. TypeScript rejects a call with neither, and a
 * call with both, so a nameless dialog does not compile.
 */
type ModalName =
    | {
          header: NonNullable<ReactNode>;
          ariaLabel?: never;
      }
    | {
          header?: never;
          ariaLabel: string;
      };

type Props = ModalName & {
    open: boolean;
    className?: string;
    onContinue?: () => void;
    onSubmit?: Function;
    onClose?: Function;
    onRemove?: Function;
    children: ReactNode;
    confirmButtonText?: ReactNode;
    confirmButtonColor?: keyof typeof colors;
    disableActions?: boolean;
    disablePadding?: boolean;
    dataTestId?: string;
    disableSubmit?: boolean;
    disableHeaderForcedLowerCase?: boolean;
    hideCancelButton?: boolean;
    openConfirmation?: boolean;
    preventCloseOnSubmit?: boolean;
    closeIconAction?: () => void;
    useContactCardStyles?: boolean;
    modalSize?: ModalSize;
    isFooterActionsMultiRow?: boolean;
    mainClassName?: string;
};

interface ModalFooterProps {
    isMultiRow: boolean;
    onRemove?: Function;
    onContinue?: () => void;
    onSubmit?: Function;
    hideCancelButton: boolean;
    confirmButtonColor?: keyof typeof colors;
    confirmButtonText?: ReactNode;
    disableSubmit?: boolean;
    cancelLabel: string;
    removeLabel: string;
    saveLabel: string;
    onClose: () => void;
    onRemoveClick: () => void;
    onContinueClick: () => void;
    onSubmitClick: () => void;
}

interface ModalHeaderProps {
    header: ReactNode;
    disableHeaderForcedLowerCase: boolean;
    closeLabel: string;
    onCloseClick: () => void;
}

const ModalHeader = ({
    header,
    disableHeaderForcedLowerCase,
    closeLabel,
    onCloseClick,
}: ModalHeaderProps) => (
    <header
        className={twMerge(
            'flex h-15 shrink-0 items-center px-4',
            header ? 'justify-between' : 'justify-end',
        )}
    >
        {header ? (
            <DialogTitle
                as="h1"
                className={twMerge(
                    'text-2xl font-bold',
                    disableHeaderForcedLowerCase
                        ? ''
                        : 'lowercase first-letter:uppercase',
                )}
            >
                {header}
            </DialogTitle>
        ) : null}

        <Button
            iconKey="close"
            color="icon"
            aria-label={closeLabel}
            onClick={onCloseClick}
            data-test-id="modal-close-button"
        />
    </header>
);

const ModalFooter = ({
    isMultiRow,
    onRemove,
    onContinue,
    onSubmit,
    hideCancelButton,
    confirmButtonColor,
    confirmButtonText,
    disableSubmit,
    cancelLabel,
    removeLabel,
    saveLabel,
    onClose,
    onRemoveClick,
    onContinueClick,
    onSubmitClick,
}: ModalFooterProps) => {
    // Cancel / Continue / Save share the room Remove leaves, each growing to
    // Button's usual 120px when there is space and shrinking to its text
    // when there is not: Remove + Cancel + Save fit a SMALL dialog on one
    // row instead of overflowing it. `!min-w-max`: Button adds its own
    // min-w after the caller's classes, so a plain min-w-max would be
    // discarded (AC-759). The multi-row footer keeps its full-width Save
    // and 120px Cancel as before.
    const buttonClassName = isMultiRow
        ? '!min-w-max w-full px-5'
        : '!min-w-max flex-1 max-w-[120px] px-5';
    const cancelClassName = isMultiRow ? 'px-5' : buttonClassName;
    const resolvedColor = confirmButtonColor ?? 'primary';
    const resolvedText = confirmButtonText || saveLabel;

    return (
        <footer
            className={twMerge(
                'flex justify-between gap-2 p-6',
                isMultiRow ? 'flex-col-reverse items-center' : '',
            )}
        >
            <div className="flex gap-2">
                {onRemove ? (
                    <Button
                        color="danger"
                        className="self-start"
                        onClick={onRemoveClick}
                        data-test-id="modal-remove-button"
                    >
                        {removeLabel}
                    </Button>
                ) : null}
            </div>

            {/* flex-wrap only matters on a narrow single-pane screen with
             *  long labels (a label never wraps inside its button any more):
             *  Save then drops to a second row instead of being clipped. */}
            <div
                className={twMerge(
                    'flex justify-end gap-2',
                    isMultiRow
                        ? 'w-full flex-col-reverse items-center'
                        : 'flex-1 flex-wrap',
                )}
            >
                {hideCancelButton ? null : (
                    <Button
                        color="secondary"
                        className={cancelClassName}
                        onClick={onClose}
                        data-test-id="modal-cancel-button"
                    >
                        {cancelLabel}
                    </Button>
                )}

                {onContinue ? (
                    <Button
                        color={resolvedColor}
                        className={buttonClassName}
                        onClick={onContinueClick}
                        data-test-id="modal-continue-button"
                    >
                        {resolvedText}
                    </Button>
                ) : null}

                {onSubmit ? (
                    <Button
                        color={resolvedColor}
                        className={buttonClassName}
                        onClick={onSubmitClick}
                        disabled={disableSubmit}
                        data-test-id="modal-submit-button"
                    >
                        {resolvedText}
                    </Button>
                ) : null}
            </div>
        </footer>
    );
};

const BaseModalContent = ({
    header,
    ariaLabel,
    className,
    open,
    onContinue,
    onSubmit,
    onClose,
    onRemove,
    children,
    confirmButtonText,
    confirmButtonColor,
    disableActions,
    dataTestId,
    disablePadding,
    disableSubmit,
    disableHeaderForcedLowerCase,
    preventCloseOnSubmit,
    hideCancelButton = false,
    openConfirmation = false,
    closeIconAction = null,
    useContactCardStyles = false,
    modalSize = modalSizes.SMALL,
    isFooterActionsMultiRow = false,
    mainClassName,
}: Props) => {
    const { i18next } = useAppContext();
    const { previousModal, closeModal } = useModal();

    const handleClose = () => {
        onClose?.();
        previousModal?.();
    };

    const handleContinue = () => {
        onContinue();
    };

    const handleRemove = () => {
        onRemove();
        !openConfirmation && closeModal();
    };

    const handleSubmit = () => {
        onSubmit();
        if (preventCloseOnSubmit) return;
        closeModal?.();
    };

    const { isSinglePaneMode } = useSinglePaneMode();

    return (
        <Transition show={open} as={Fragment}>
            <Dialog
                static={true}
                as="div"
                className="relative z-20"
                aria-label={ariaLabel}
                onClose={handleClose}
                data-test-id={dataTestId}
            >
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900 opacity-70 transition-opacity dark:bg-gray-100" />
                </TransitionChild>

                <div className="fixed inset-0 z-30 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel
                                className={twMerge(
                                    isSinglePaneMode
                                        ? 'fixed !size-full'
                                        : `relative flex max-h-[80dvh] ${modalSize}`,
                                    'shadow-base transform flex-col overflow-hidden rounded-sm bg-white text-start transition-all',
                                    useContactCardStyles
                                        ? 'dark:bg-white'
                                        : 'dark:bg-gray-200',
                                    className,
                                )}
                                data-test-id="modal-dialog"
                            >
                                <ModalHeader
                                    header={header}
                                    disableHeaderForcedLowerCase={
                                        disableHeaderForcedLowerCase
                                    }
                                    closeLabel={i18next.t('close')}
                                    onCloseClick={
                                        closeIconAction || handleClose
                                    }
                                />

                                <main
                                    className={twMerge(
                                        // py-1 leaves room for the focus
                                        // ring of a control at the top or the
                                        // bottom, which the scroller cuts off.
                                        'scrollbar h-full flex-1 overflow-y-scroll py-1',
                                        disableActions
                                            ? 'max-h-[calc(100%-60px)]'
                                            : 'max-h-[calc(100%-140px)]',
                                        disablePadding ? '' : 'px-4',
                                        mainClassName,
                                    )}
                                >
                                    {children}
                                </main>

                                {disableActions ? null : (
                                    <ModalFooter
                                        isMultiRow={isFooterActionsMultiRow}
                                        onRemove={onRemove}
                                        onContinue={onContinue}
                                        onSubmit={onSubmit}
                                        hideCancelButton={hideCancelButton}
                                        confirmButtonColor={confirmButtonColor}
                                        confirmButtonText={confirmButtonText}
                                        disableSubmit={disableSubmit}
                                        cancelLabel={i18next.t('cancel')}
                                        removeLabel={i18next.t('remove')}
                                        saveLabel={i18next.t('navigate.save')}
                                        onClose={handleClose}
                                        onRemoveClick={handleRemove}
                                        onContinueClick={handleContinue}
                                        onSubmitClick={handleSubmit}
                                    />
                                )}
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

// Context passes through portals, so a dialog that a list row opens would
// otherwise take the row's Tab stop state.
const BaseModal = (props: Props) => (
    <RowTabStopContext.Provider value={undefined}>
        <BaseModalContent {...props} />
    </RowTabStopContext.Provider>
);

export default BaseModal;
